import React, { useRef } from 'react';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from './firebase';
import { QRCodeSVG } from 'qrcode.react';
import { Receipt, Scan, Link2, BarChart3, ChevronRight, ArrowDown, Users, Zap, Check } from 'lucide-react';
import './LandingPage.css';

interface Props {
  onSignIn: () => void;
  error: string;
}

export default function LandingPage({ onSignIn, error }: Props) {
  const howRef = useRef<HTMLElement>(null);

  async function handleSignIn() {
    try {
      await signInWithPopup(auth, googleProvider);
      onSignIn();
    } catch (e) {
      console.error(e);
    }
  }

  return (
    <div className="lp-page">

      {/* ── Nav ── */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <div className="lp-nav-logo">
            <div className="lp-nav-icon">
              <Receipt size={18} color="white" strokeWidth={2} />
            </div>
            <span className="lp-nav-brand">XpenseMate</span>
          </div>
          <button className="lp-nav-signin" onClick={handleSignIn}>
            Sign in with Google
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="lp-hero">
        <div className="lp-hero-left">
          <div className="lp-hero-badge">
            <Zap size={13} /> AI-powered expense splitting
          </div>
          <h1 className="lp-hero-title">
            Group expenses,<br />
            <span className="lp-hero-accent">done right.</span>
          </h1>
          <p className="lp-hero-sub">
            AI scans receipts instantly. Your group needs no account, no app.
            Settlement is calculated in real-time.
          </p>
          <div className="lp-hero-ctas">
            <button className="lp-btn-primary" onClick={handleSignIn}>
              Get started free <ChevronRight size={18} />
            </button>
            <button
              className="lp-btn-ghost"
              onClick={() => howRef.current?.scrollIntoView({ behavior: 'smooth' })}
            >
              How it works <ArrowDown size={16} />
            </button>
          </div>
          {error && <p className="lp-error">{error}</p>}
          <div className="lp-hero-proof">
            <Check size={14} className="lp-check" /> Free &nbsp;·&nbsp;
            <Check size={14} className="lp-check" /> No app for members &nbsp;·&nbsp;
            <Check size={14} className="lp-check" /> AI receipt scanning
          </div>
        </div>

        <div className="lp-hero-right">
          {/* Mock event card */}
          <div className="lp-mock-card">
            <div className="lp-mock-header">
              <div className="lp-mock-dot red" /><div className="lp-mock-dot yellow" /><div className="lp-mock-dot green" />
            </div>
            <div className="lp-mock-event">
              <div className="lp-mock-event-name">Vegas Trip 🎲</div>
              <div className="lp-mock-event-meta">Aria Resort · Jun 14, 2026</div>
              <div className="lp-mock-stats">
                <div className="lp-mock-stat">
                  <div className="lp-mock-stat-val">4<span>/6</span></div>
                  <div className="lp-mock-stat-lbl">submitted</div>
                </div>
                <div className="lp-mock-stat accent">
                  <div className="lp-mock-stat-val">$82.40</div>
                  <div className="lp-mock-stat-lbl">per person</div>
                </div>
              </div>
              <div className="lp-mock-progress">
                <div className="lp-mock-progress-fill" style={{ width: '67%' }} />
              </div>
            </div>
            <div className="lp-mock-qr">
              <QRCodeSVG
                value="https://xpensemate.com/event/XPM-DEMO"
                size={100}
                bgColor="#1a1a2e"
                fgColor="#a5b4fc"
                level="M"
              />
              <div className="lp-mock-qr-text">
                <div className="lp-mock-qr-label">Scan to submit</div>
                <div className="lp-mock-qr-url">xpensemate.com/event/XPM-V3G5</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="lp-how" ref={howRef} id="how">
        <div className="lp-section-inner">
          <p className="lp-section-label">How it works</p>
          <h2 className="lp-section-title">Up and running in 3 steps</h2>
          <div className="lp-steps">
            <div className="lp-step">
              <div className="lp-step-num">1</div>
              <div className="lp-step-icon"><Link2 size={22} /></div>
              <h3>Create an event</h3>
              <p>Set the name, venue, date, and how many people are splitting. You get a unique link and QR code instantly.</p>
            </div>
            <div className="lp-step-arrow">→</div>
            <div className="lp-step">
              <div className="lp-step-num">2</div>
              <div className="lp-step-icon"><Scan size={22} /></div>
              <h3>Members scan & submit</h3>
              <p>Group members scan the QR or open the link. They photo their receipt — AI extracts every line item automatically.</p>
            </div>
            <div className="lp-step-arrow">→</div>
            <div className="lp-step">
              <div className="lp-step-num">3</div>
              <div className="lp-step-icon"><BarChart3 size={22} /></div>
              <h3>See the settlement</h3>
              <p>You see live who paid what, who owes the pool, and exactly how to settle up. No spreadsheets, no math.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="lp-features">
        <div className="lp-section-inner">
          <p className="lp-section-label">Why XpenseMate</p>
          <h2 className="lp-section-title">Built different from the start</h2>
          <div className="lp-feature-grid">
            <div className="lp-feature-card">
              <div className="lp-feature-icon purple">
                <Scan size={24} />
              </div>
              <h3>AI receipt scanning</h3>
              <p>Take a photo of any receipt. Our AI extracts every item, amount, and tax line automatically — no manual entry.</p>
            </div>
            <div className="lp-feature-card">
              <div className="lp-feature-icon green">
                <Users size={24} />
              </div>
              <h3>Zero friction for members</h3>
              <p>No app to download. No account to create. Members just scan a QR code or tap a link. That's it.</p>
            </div>
            <div className="lp-feature-card">
              <div className="lp-feature-icon blue">
                <Zap size={24} />
              </div>
              <h3>Real-time settlement</h3>
              <p>The moment someone submits, the settlement updates live. See exactly who owes what before dinner ends.</p>
            </div>
            <div className="lp-feature-card">
              <div className="lp-feature-icon orange">
                <BarChart3 size={24} />
              </div>
              <h3>Group vs personal split</h3>
              <p>Mark each item as group or personal. Shared appetizers split equally; your steak is yours alone.</p>
            </div>
            <div className="lp-feature-card">
              <div className="lp-feature-icon pink">
                <Link2 size={24} />
              </div>
              <h3>QR code sharing</h3>
              <p>Display the QR at the table. Everyone scans and submits their own receipt. No chasing people down.</p>
            </div>
            <div className="lp-feature-card">
              <div className="lp-feature-icon teal">
                <Receipt size={24} />
              </div>
              <h3>Event-based, not ongoing</h3>
              <p>Clean start, clean end. One dinner, one trip, one event — no lingering debt graphs with old friends.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA banner ── */}
      <section className="lp-cta">
        <div className="lp-cta-inner">
          <h2 className="lp-cta-title">Ready to try it?</h2>
          <p className="lp-cta-sub">Free to use. No credit card. Create your first event in under a minute.</p>
          <button className="lp-btn-primary large" onClick={handleSignIn}>
            Sign in with Google &nbsp;<ChevronRight size={18} />
          </button>
          <p className="lp-cta-note">Members joining your event don't need an account.</p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-logo">
            <div className="lp-nav-icon small">
              <Receipt size={14} color="white" strokeWidth={2} />
            </div>
            <span>XpenseMate</span>
          </div>
          <div className="lp-footer-links">
            <a href="/privacy" className="lp-footer-link">Privacy Policy</a>
            <span className="lp-footer-sep">·</span>
            <a href="/disclaimer" className="lp-footer-link">Disclaimer &amp; Terms</a>
            <span className="lp-footer-sep">·</span>
            <span className="lp-footer-copy">Free for the web · {new Date().getFullYear()}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
